/**
 * 社印・ロゴの画像を、登録できる形（PNG か JPEG、2MB・1500px まで。API の決まり、025）にそろえる。
 * 2026-09-24 射場さん「PNG を取り込めなかった」→ 決まりに合わない画像も、この端末で読めれば縮めて登録できるようにした。
 *   - PNG・JPEG で決まりの中なら、そのまま送る（中身を変えない）
 *   - 大きい（1500px を超える・2MB を超える）、WebP・GIF・BMP・SVG、種類が分からない（拡張子なし）ものは、
 *     この端末で読んで長い辺を 1500px 以下に縮め、PNG にする（透明はそのまま）。写真のような PNG で 2MB を超えるときは、
 *     元が JPEG なら JPEG に、そうでなければもう少し縮める
 *   - 読めない（PDF・Illustrator・Word、HEIC を読めないブラウザ）ときは null
 * 画面の CSP（img-src 'self' data:）で blob: の URL は使えないので、ラスター画像は createImageBitmap、SVG は data: URL で読む
 */
export const IMAGE_MAX_BYTES = 2 * 1024 * 1024;
export const IMAGE_MAX_PX = 1500;
/** SVG は大きさを持たないことがあるので、長い辺をこの大きさで描く（ロゴの 12mm に十分） */
const SVG_TARGET_PX = 1200;

export type ImageContentType = "image/png" | "image/jpeg";
export type PreparedImage = { blob: Blob; contentType: ImageContentType; width: number; height: number; resized: boolean };

type Decoded = { source: CanvasImageSource; width: number; height: number; close(): void };

/** 先頭のバイトで種類を見る（ファイル名や OS の判定に頼らない） */
export function sniffImageType(head: Uint8Array): ImageContentType | "image/svg+xml" | null {
  if (head.length >= 8 && head[0] === 0x89 && head[1] === 0x50 && head[2] === 0x4e && head[3] === 0x47 && head[4] === 0x0d && head[5] === 0x0a && head[6] === 0x1a && head[7] === 0x0a) return "image/png";
  if (head.length >= 3 && head[0] === 0xff && head[1] === 0xd8 && head[2] === 0xff) return "image/jpeg";
  const text = new TextDecoder("utf-8", { fatal: false }).decode(head.slice(0, 512)).replace(/^﻿/, "").trimStart().toLowerCase();
  if (text.startsWith("<svg") || (text.startsWith("<?xml") && text.includes("<svg"))) return "image/svg+xml";
  return null;
}

/** 長い辺を max 以下に（小さいものは大きくしない） */
export function fitWithin(width: number, height: number, max: number): { width: number; height: number } {
  const s = Math.min(1, max / Math.max(width, height));
  return { width: Math.max(1, Math.round(width * s)), height: Math.max(1, Math.round(height * s)) };
}

async function decodeRaster(file: Blob): Promise<Decoded | null> {
  try {
    const bmp = await createImageBitmap(file);
    return { source: bmp, width: bmp.width, height: bmp.height, close: () => bmp.close() };
  } catch {
    return null;
  }
}

async function decodeSvg(file: Blob): Promise<Decoded | null> {
  const url = await new Promise<string | null>((resolve) => {
    const r = new FileReader();
    r.onload = () => resolve(typeof r.result === "string" ? r.result : null);
    r.onerror = () => resolve(null);
    r.readAsDataURL(new Blob([file], { type: "image/svg+xml" }));
  });
  if (!url) return null;
  const img = new Image();
  img.src = url;
  try {
    await img.decode();
  } catch {
    return null;
  }
  let w = img.naturalWidth;
  let h = img.naturalHeight;
  if (!w || !h) {
    // 幅・高さの無い SVG は viewBox の比で描く
    const text = await file.text();
    const vb = /viewBox\s*=\s*["']\s*[-\d.]+[\s,]+[-\d.]+[\s,]+([\d.]+)[\s,]+([\d.]+)/i.exec(text);
    const ratio = vb ? Number(vb[1]) / Number(vb[2]) : 1;
    w = ratio >= 1 ? SVG_TARGET_PX : Math.round(SVG_TARGET_PX * ratio);
    h = ratio >= 1 ? Math.round(SVG_TARGET_PX / ratio) : SVG_TARGET_PX;
  } else {
    // 小さく書かれた SVG（width="200" など）も紙でにじまないよう大きく描く
    const s = SVG_TARGET_PX / Math.max(w, h);
    w = Math.round(w * s);
    h = Math.round(h * s);
  }
  img.width = w;
  img.height = h;
  return { source: img, width: w, height: h, close: () => undefined };
}

function toBlob(canvas: HTMLCanvasElement, type: ImageContentType, quality?: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

function draw(d: Decoded, width: number, height: number, opaque: boolean): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d")!;
  if (opaque) {
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, width, height);
  }
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(d.source, 0, 0, width, height);
  return canvas;
}

/** 登録できる形にする。読めなければ null */
export async function prepareImage(file: Blob, opts: { maxPx?: number } = {}): Promise<PreparedImage | null> {
  const maxPx = opts.maxPx ?? IMAGE_MAX_PX; // 書式の画像（046）は 2400px まで（下敷きのスキャンが読める大きさ）
  const head = new Uint8Array(await file.slice(0, 512).arrayBuffer());
  const kind = sniffImageType(head);
  const d = kind === "image/svg+xml" ? await decodeSvg(file) : await decodeRaster(file);
  if (!d || !d.width || !d.height) return null;
  try {
    // 決まりの中の PNG・JPEG はそのまま
    if ((kind === "image/png" || kind === "image/jpeg") && file.size <= IMAGE_MAX_BYTES && Math.max(d.width, d.height) <= maxPx) {
      return { blob: kind === file.type ? file : new Blob([file], { type: kind }), contentType: kind, width: d.width, height: d.height, resized: false };
    }
    let max = maxPx;
    for (let i = 0; i < 8; i++) {
      const size = fitWithin(d.width, d.height, max);
      const png = await toBlob(draw(d, size.width, size.height, false), "image/png");
      if (png && png.size <= IMAGE_MAX_BYTES) return { blob: png, contentType: "image/png", ...size, resized: true };
      // 元が JPEG（透明が無い写真）なら JPEG で
      if (kind === "image/jpeg") {
        const jpg = await toBlob(draw(d, size.width, size.height, true), "image/jpeg", 0.9);
        if (jpg && jpg.size <= IMAGE_MAX_BYTES) return { blob: jpg, contentType: "image/jpeg", ...size, resized: true };
      }
      max = Math.round(max * 0.8);
    }
    return null;
  } finally {
    d.close();
  }
}

/**
 * 行の写真（#117、docs/44 §6）：撮った写真をこの端末で描き直して、表示用（長辺 1600px）と小さい絵（長辺 400px）の JPEG 2枚にする。
 * 描き直すので撮った場所・機種などの情報（Exif）は残らない（API でも取り除く）。向きは写真の情報どおり（imageOrientation: from-image）。
 * 写真をサーバーで縮めない（CLAUDE.md「サーバーの制約」）。読めない（HEIC を読めないブラウザなど）ときは null
 */
export type PhotoPair = { display: Blob; thumb: Blob; width: number; height: number };
export async function photoPair(file: Blob, limits: { displayPx: number; thumbPx: number; displayBytes: number; thumbBytes: number; quality: number }): Promise<PhotoPair | null> {
  let bmp: ImageBitmap;
  try {
    bmp = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    return null;
  }
  try {
    const jpeg = async (max: number, maxBytes: number): Promise<{ blob: Blob; width: number; height: number } | null> => {
      const size = fitWithin(bmp.width, bmp.height, max);
      const c = document.createElement("canvas");
      c.width = size.width;
      c.height = size.height;
      const ctx = c.getContext("2d");
      if (!ctx) return null;
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, size.width, size.height);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(bmp, 0, 0, size.width, size.height);
      for (const q of [limits.quality, 0.7, 0.6, 0.5]) {
        const blob = await toBlob(c, "image/jpeg", q);
        if (blob && blob.size <= maxBytes) return { blob, ...size };
      }
      return null;
    };
    const d = await jpeg(limits.displayPx, limits.displayBytes);
    const t = await jpeg(limits.thumbPx, limits.thumbBytes);
    return d && t ? { display: d.blob, thumb: t.blob, width: d.width, height: d.height } : null;
  } finally {
    bmp.close();
  }
}

/** Blob → data: URL（画面の CSP は img-src 'self' data: なので、送る前の写真は data: で見せる） */
export function dataUrlOf(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => (typeof r.result === "string" ? resolve(r.result) : reject(new Error("read")));
    r.onerror = () => reject(r.error ?? new Error("read"));
    r.readAsDataURL(blob);
  });
}
